import { gmailApprovalsResponseSchema, gmailDecisionResponseSchema, type GmailApproval } from "@/features/chat/gmailApprovalSchemas";
import {
  conversationAccessTokenResponseSchema,
  conversationExecutionNodesResponseSchema,
  conversationEvidenceResponseSchema,
  conversationMessagesResponseSchema,
  conversationResponseSchema,
  conversationsResponseSchema,
  conversationStartSessionResponseSchema,
  conversationTurnsResponseSchema,
} from "@/features/chat/conversationSchemas";
import { parseConversationFreshTasks } from "@/features/chat/conversationFreshTasks";
import {
  conversationWorkflowEventsResponseSchema,
  conversationWorkflowsResponseSchema,
} from "@/features/chat/conversationWorkflowSchemas";
import { baseApi } from "@/shared/api/baseApi";

export interface ConversationScope {
  conversationId: string;
  organizationId: string;
  shopBindingId: string;
  userId: string;
}

interface CreateConversationInput {
  initialResourceIds: ReadonlyArray<string>;
  organizationId: string;
  shopBindingId: string;
  title: string;
  userId: string;
}

interface ConversationPageParam {
  beforeSequence: number;
}

interface StartConversationInput extends ConversationScope {
  clientData: Readonly<Record<string, unknown>>;
  taskId: string;
}

interface RetryConversationWorkflowStepInput extends ConversationScope {
  workflowId: string;
  stepId: string;
  expectedWorkflowRevision: number;
  expectedStepRevision: number;
  expectedAttempt: number;
}

interface ConversationWorkflowEventsInput extends ConversationScope {
  workflowId: string;
  afterRevision: number;
}

function conversationsPath(organizationId: string) {
  return `/v1/organizations/${encodeURIComponent(organizationId)}/conversations`;
}

function conversationPath(input: ConversationScope) {
  const params = new URLSearchParams({ shop_binding_id: input.shopBindingId });
  return `${conversationsPath(input.organizationId)}/${encodeURIComponent(input.conversationId)}?${params.toString()}`;
}

function appendConversationPath(input: ConversationScope, suffix: string) {
  const path = `${conversationsPath(input.organizationId)}/${encodeURIComponent(input.conversationId)}/${suffix}`;
  return `${path}?${new URLSearchParams({ shop_binding_id: input.shopBindingId }).toString()}`;
}

function pageUrl(
  input: ConversationScope,
  resource: "messages" | "turns",
  pageParam: ConversationPageParam,
) {
  const params = new URLSearchParams({ limit: resource === "messages" ? "100" : "300" });
  params.set("shop_binding_id", input.shopBindingId);
  if (pageParam.beforeSequence > 0) {
    params.set("before_sequence", String(pageParam.beforeSequence));
  }
  return `${conversationsPath(input.organizationId)}/${encodeURIComponent(input.conversationId)}/${resource}?${params.toString()}`;
}

export const conversationApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConversationGmailApprovals: builder.query<ReturnType<typeof gmailApprovalsResponseSchema.parse>, ConversationScope>({
      keepUnusedDataFor: 0,
      query: (scope) => appendConversationPath(scope, "gmail-approvals"),
      transformResponse: (response) => gmailApprovalsResponseSchema.parse(response),
    }),
    decideConversationGmailApproval: builder.mutation<ReturnType<typeof gmailDecisionResponseSchema.parse>, ConversationScope & { approval: GmailApproval; decision: "approved" | "rejected" }>({
      query: ({ approval, decision, ...scope }) => ({
        url: appendConversationPath(scope, `gmail-approvals/${encodeURIComponent(approval.execution_id)}/decision`),
        method: "POST",
        body: {
          decision,
          expected_plan_hash: approval.plan_hash,
          expected_state_revision: approval.state_revision,
          resource_ids: approval.resource_ids,
          idempotency_key: `${decision}:${approval.execution_id}:${approval.state_revision}:${approval.plan_hash}`,
        },
      }),
      transformResponse: (response) => gmailDecisionResponseSchema.parse(response),
    }),
    getConversationEvidence: builder.query<ReturnType<typeof conversationEvidenceResponseSchema.parse>,
      Omit<ConversationScope, "userId"> & { assistantMessageId: string; cursor?: string }>({
      keepUnusedDataFor: 0,
      query: ({ assistantMessageId, cursor, ...scope }) => `${appendConversationPath({ ...scope, userId: "" }, "evidence")}&assistant_message_id=${encodeURIComponent(assistantMessageId)}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      transformResponse: (response) => conversationEvidenceResponseSchema.parse(response),
    }),
    createConversation: builder.mutation<
      ReturnType<typeof conversationResponseSchema.parse>,
      CreateConversationInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: `${input.userId}:${input.organizationId}:${input.shopBindingId}`, type: "Conversations" },
      ],
      query: ({ initialResourceIds, organizationId, shopBindingId, title }) => ({
        body: {
          initial_resource_refs: initialResourceIds,
          metadata: { client: "merry-web", visibility: "creator_private" },
          shop_binding_id: shopBindingId,
          title,
        },
        method: "POST",
        url: conversationsPath(organizationId),
      }),
      transformResponse: (response) => conversationResponseSchema.parse(response),
    }),
    getConversation: builder.query<
      ReturnType<typeof conversationResponseSchema.parse>,
      ConversationScope
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "Conversation" },
      ],
      query: (input) => `${conversationPath(input)}&include_messages=false`,
      transformResponse: (response) => conversationResponseSchema.parse(response),
    }),
    getConversationMessages: builder.infiniteQuery<
      ReturnType<typeof conversationMessagesResponseSchema.parse>,
      ConversationScope,
      ConversationPageParam
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) =>
          lastPage.hasMore && lastPage.nextBeforeSequence
            ? { beforeSequence: lastPage.nextBeforeSequence }
            : undefined,
        initialPageParam: { beforeSequence: 0 },
      },
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "ConversationMessages" },
      ],
      query: ({ pageParam, queryArg }) =>
        pageUrl(queryArg, "messages", pageParam),
      transformResponse: (response) =>
        conversationMessagesResponseSchema.parse(response),
    }),
    getConversationExecutionNodes: builder.query<
      ReturnType<typeof conversationExecutionNodesResponseSchema.parse>,
      ConversationScope
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "ConversationExecutionNodes" },
      ],
      query: (input) => appendConversationPath(input, "execution-nodes"),
      transformResponse: (response) =>
        conversationExecutionNodesResponseSchema.parse(response),
    }),
    getConversationWorkflows: builder.query<
      ReturnType<typeof conversationWorkflowsResponseSchema.parse>,
      ConversationScope
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "ConversationWorkflows" },
      ],
      query: (input) => appendConversationPath(input, "workflows"),
      transformResponse: (response) =>
        conversationWorkflowsResponseSchema.parse(response),
    }),
    getConversationWorkflowEvents: builder.query<
      ReturnType<typeof conversationWorkflowEventsResponseSchema.parse>,
      ConversationWorkflowEventsInput
    >({
      keepUnusedDataFor: 0,
      query: ({ afterRevision, workflowId, ...scope }) => {
        const path = appendConversationPath(
          scope,
          `workflows/${encodeURIComponent(workflowId)}/events`,
        );
        return `${path}&after_revision=${encodeURIComponent(String(afterRevision))}`;
      },
      transformResponse: (response) =>
        conversationWorkflowEventsResponseSchema.parse(response),
    }),
    retryConversationWorkflowStep: builder.mutation<
      ReturnType<typeof conversationWorkflowsResponseSchema.shape.workflows.element.parse>,
      RetryConversationWorkflowStepInput
    >({
      invalidatesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "ConversationWorkflows" },
      ],
      query: ({
        expectedAttempt,
        expectedStepRevision,
        expectedWorkflowRevision,
        stepId,
        workflowId,
        ...scope
      }) => ({
        body: {
          expected_attempt: expectedAttempt,
          expected_step_revision: expectedStepRevision,
          expected_workflow_revision: expectedWorkflowRevision,
        },
        method: "POST",
        url: appendConversationPath(
          scope,
          `workflows/${encodeURIComponent(workflowId)}/steps/${encodeURIComponent(stepId)}/retry`,
        ),
      }),
      transformResponse: (response: unknown) => {
        const envelope = response as { workflow?: unknown };
        return conversationWorkflowsResponseSchema.shape.workflows.element.parse(envelope.workflow);
      },
    }),
    getConversationFreshTasks: builder.query<
      ReturnType<typeof parseConversationFreshTasks>,
      ConversationScope
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "ConversationFreshTasks" },
      ],
      query: (input) => appendConversationPath(input, "async-tasks"),
      transformResponse: parseConversationFreshTasks,
    }),
    getConversationTurns: builder.infiniteQuery<
      ReturnType<typeof conversationTurnsResponseSchema.parse>,
      ConversationScope,
      ConversationPageParam
    >({
      infiniteQueryOptions: {
        getNextPageParam: (lastPage) =>
          lastPage.hasMore && lastPage.nextBeforeSequence
            ? { beforeSequence: lastPage.nextBeforeSequence }
            : undefined,
        initialPageParam: { beforeSequence: 0 },
      },
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: input.conversationId, type: "ConversationTurns" },
      ],
      query: ({ pageParam, queryArg }) => pageUrl(queryArg, "turns", pageParam),
      transformResponse: (response) =>
        conversationTurnsResponseSchema.parse(response),
    }),
    listConversations: builder.query<
      ReturnType<typeof conversationsResponseSchema.parse>,
      Omit<ConversationScope, "conversationId">
    >({
      keepUnusedDataFor: 0,
      providesTags: (_result, _error, input) => [
        { id: `${input.userId}:${input.organizationId}:${input.shopBindingId}`, type: "Conversations" },
      ],
      query: ({ organizationId, shopBindingId }) =>
        `${conversationsPath(organizationId)}?${new URLSearchParams({ shop_binding_id: shopBindingId }).toString()}`,
      transformResponse: (response) => conversationsResponseSchema.parse(response),
    }),
    mintConversationAccessToken: builder.mutation<
      ReturnType<typeof conversationAccessTokenResponseSchema.parse>,
      ConversationScope
    >({
      query: (input) => ({
        method: "POST",
        url: appendConversationPath(input, "trigger/access-token"),
      }),
      transformResponse: (response) =>
        conversationAccessTokenResponseSchema.parse(response),
    }),
    startConversationSession: builder.mutation<
      ReturnType<typeof conversationStartSessionResponseSchema.parse>,
      StartConversationInput
    >({
      query: ({ clientData, taskId, ...input }) => ({
        body: { client_data: clientData, task_id: taskId },
        method: "POST",
        url: appendConversationPath(input, "trigger/start-session"),
      }),
      transformResponse: (response) =>
        conversationStartSessionResponseSchema.parse(response),
    }),
  }),
});

export const {
  useGetConversationGmailApprovalsQuery,
  useDecideConversationGmailApprovalMutation,
  useCreateConversationMutation,
  useGetConversationExecutionNodesQuery,
  useGetConversationEvidenceQuery,
  useLazyGetConversationWorkflowEventsQuery,
  useGetConversationWorkflowsQuery,
  useGetConversationFreshTasksQuery,
  useGetConversationMessagesInfiniteQuery,
  useGetConversationQuery,
  useGetConversationTurnsInfiniteQuery,
  useListConversationsQuery,
  useMintConversationAccessTokenMutation,
  useRetryConversationWorkflowStepMutation,
  useStartConversationSessionMutation,
} = conversationApi;
